import {
  useMemo,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  type FormEvent,
  type ReactNode,
} from 'react';
import {
  LifeBuoy,
  Send,
  MessageSquare,
  Loader2,
  WifiOff,
  CheckCircle2,
  RotateCcw,
  UserPlus,
  UserMinus,
  Forward,
  Star,
  Bell,
  Search,
  Zap,
  Tag,
  Lock,
  X,
  Clock,
  MoreHorizontal,
  PanelRight,
  RefreshCw,
  ChevronLeft,
  ChevronDown,
  ClipboardList,
  Paperclip,
  AlertTriangle,
  SpellCheck2,
  Sparkles,
  User,
  Pause,
  Pencil,
  Scissors,
  CornerDownRight,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import {
  useConversations,
  useMessages,
  useSendMessage,
  useSendAudio,
  useSendAttachment,
  useTypingSignal,
  useThreadHistory,
  useToggleStatus,
  useAssign,
  useTransfer,
  useAgents,
  useLabels,
  useSetLabels,
  usePin,
  useCanned,
  useMarkConversationRead,
  useAcceptTransfer,
  useCorrectDraft,
  useSuggestReply,
} from '@/hooks/useSupport';
import { useNotifications } from '@/hooks/useNotifications';
import { supportApi, mensagemDeErro, codigoDeErro } from '@/api/endpoints/support';
import { useSearchParams } from 'react-router-dom';
import { ProducerPanel } from './ProducerPanel';
import { informarConversaAtiva } from '@/lib/pushNativo';
import { MessageMedia } from './midia/MessageMedia';
import { MediaViewer } from './midia/MediaViewer';
import { infoDoArquivo, formatarTamanho, type ItemMidia } from './midia/arquivo';
import { EditorImagem } from './midia/EditorImagem';
import { EditorVideo, type TrechoVideo } from './midia/EditorVideo';
import { AudioRecorder, BotaoGravar } from './AudioRecorder';
import { flagMeta, FILTER_FLAGS } from './flags';
import { descreverFalha } from './falhaEnvio';
import { estadoDaJanela, duracaoCurta, JANELA_URGENTE_MS } from './janela';
import type { EstadoJanela } from './janela';
import type { Conversation, ChatwootMessage } from '@/api/endpoints/support';

// `curto`: rótulo da aba no celular, onde as quatro dividem uma linha estreita.
const STATUS_FILTERS: { key: string; label: string; curto?: string; noun: string }[] = [
  { key: 'open', label: 'Em atendimento', curto: 'Atendendo', noun: 'atendimento' },
  { key: 'pending', label: 'Pendentes', noun: 'pendente' },
  { key: 'snoozed', label: 'Pausadas', noun: 'pausada' },
  { key: 'resolved', label: 'Resolvidas', noun: 'resolvida' },
];

// Chave de idempotência do envio. O navegador do painel sempre tem
// crypto.randomUUID (contexto seguro); o fallback é só pra teste.
function novoIdDeEnvio(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const CATEGORY_FLAGS = ['tecnico', 'financeiro', 'novo-usuario'];

// Início da mensagem de encerramento que o bot envia ao resolver. É por ela que
// o painel sabe onde um atendimento terminou e outro começou — a conversa é uma
// só por contato, então sem esse corte tudo vira um bloco contínuo e o atendente
// lê o problema da semana passada achando que é o de agora.
const TEXTO_ENCERRAMENTO = 'Seu atendimento foi encerrado';

function ehEncerramento(m: ChatwootMessage): boolean {
  return m.message_type === 1 && !!m.content?.startsWith(TEXTO_ENCERRAMENTO);
}

function initials(name?: string) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

function timeAgo(ts?: number) {
  if (!ts) return '';
  const diff = Date.now() / 1000 - ts;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function clockTime(ts?: number) {
  if (!ts) return '';
  return new Date(ts * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function dayLabel(ts?: number) {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (same(d, today)) return 'Hoje';
  if (same(d, yest)) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });
}

function lastPreview(conv: Conversation): string {
  const msg = conv.last_non_activity_message || conv.messages?.[conv.messages.length - 1];
  return msg?.content || 'Sem mensagens';
}

function isPinned(conv: Conversation): boolean {
  return !!conv.custom_attributes?.pinned;
}

function waitingMinutes(conv: Conversation): number | null {
  const since = conv.waiting_since;
  if (!since) return null;
  const m = Math.floor(Date.now() / 1000 - since) / 60;
  return m >= 1 ? Math.floor(m) : null;
}

function categoryOf(conv?: Conversation | null): string | null {
  return conv?.labels?.find((l) => CATEGORY_FLAGS.includes(l)) ?? null;
}

// O bot só grava `bot_category` depois que o cliente escolhe o assunto, e a
// conversa sai de `pending` no handoff: as duas coisas juntas = triagem pronta.
function triageDone(conv: Conversation): boolean {
  return !!conv.custom_attributes?.bot_category && conv.status !== 'pending';
}

// Cliente aguardando resposta: conversa não resolvida cuja última mensagem real
// (ignora eventos de sistema) é do cliente (message_type 0 = incoming).
function isUnanswered(conv: Conversation): boolean {
  if (conv.status === 'resolved') return false;
  return conv.last_non_activity_message?.message_type === 0;
}

function FlagBadges({ labels, max }: { labels?: string[]; max?: number }) {
  if (!labels?.length) return null;
  const shown = max ? labels.slice(0, max) : labels;
  return (
    <>
      {shown.map((l) => {
        const m = flagMeta(l);
        return (
          <span
            key={l}
            className={cn(
              'flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none',
              m.cls
            )}
          >
            <span className={cn('size-1.5 rounded-full', m.dot)} />
            {m.label}
          </span>
        );
      })}
      {max && labels.length > max && (
        <span className="text-[10px] text-text-tertiary">+{labels.length - max}</span>
      )}
    </>
  );
}

function ConversationRow({
  conv,
  active,
  typing,
  reunidas = 1,
  onClick,
}: {
  conv: Conversation;
  active: boolean;
  typing?: boolean;
  /** Quantos atendimentos deste contato estão reunidos nesta linha. */
  reunidas?: number;
  onClick: () => void;
}) {
  const name = conv.meta?.sender?.name || conv.meta?.sender?.phone_number || `Conversa #${conv.id}`;
  const phone = conv.meta?.sender?.phone_number;
  const unread = conv.unread_count || 0;
  const waiting = waitingMinutes(conv);
  const pinned = isPinned(conv);
  const cat = categoryOf(conv);
  const unanswered = isUnanswered(conv);
  const triaged = triageDone(conv);
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex w-full items-center gap-3 overflow-hidden rounded-xl border px-3 py-2.5 text-left transition-colors',
        active ? 'border-primary/60 bg-primary/5' : 'border-transparent hover:bg-muted'
      )}
    >
      {/* Indicador de "cliente não respondido": barra vermelha à esquerda */}
      {unanswered && <span className="absolute inset-y-1.5 left-0 w-1 rounded-full bg-secondary" />}
      <span className="relative flex size-11 shrink-0 items-center justify-center rounded-full bg-tertiary/15 text-sm font-semibold text-tertiary">
        {initials(conv.meta?.sender?.name)}
        {pinned && (
          <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-amber-400 text-white">
            <Star size={9} fill="currentColor" />
          </span>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5">
            {unanswered && (
              <span
                className="size-2 shrink-0 rounded-full bg-secondary"
                title="Cliente aguardando resposta"
              />
            )}
            <span className="truncate text-sm font-semibold text-text-title">{name}</span>
            {reunidas > 1 && (
              <span
                className="shrink-0 rounded-full bg-elevated px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-text-secondary"
                title={`${reunidas} atendimentos deste contato reunidos aqui`}
              >
                {reunidas}
              </span>
            )}
          </span>
        </span>
        {phone && name !== phone && (
          <span className="block truncate text-xs text-text-tertiary">{phone}</span>
        )}
        <span className="mt-1 flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5">
            {typing ? (
              <span className="text-xs font-medium text-tertiary">digitando…</span>
            ) : cat ? (
              <FlagBadges labels={[cat]} />
            ) : (
              <span className="truncate text-xs text-text-secondary">{lastPreview(conv)}</span>
            )}
            {triaged && (
              <span
                className="flex shrink-0 items-center gap-0.5 rounded-full bg-tertiary/10 px-1.5 py-0.5 text-[10px] font-medium text-tertiary"
                title="Triagem concluída pelo bot"
              >
                <ClipboardList size={9} /> triado
              </span>
            )}
            {waiting != null && (
              <span className="flex items-center gap-0.5 rounded-full bg-red-500/10 px-1.5 py-0.5 text-[10px] font-medium text-red-500">
                <Clock size={9} /> {waiting}m
              </span>
            )}
          </span>
          {unread > 0 && (
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
              {unread}
            </span>
          )}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1.5 self-center">
        <span className="text-[11px] text-text-tertiary">{timeAgo(conv.timestamp)}</span>
        {conv.meta?.assignee && (
          <span
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary"
            title={`Em atendimento por ${conv.meta.assignee.name}`}
          >
            <User size={13} /> {conv.meta.assignee.name.split(' ')[0]}
          </span>
        )}
      </span>
    </button>
  );
}

// Formatação do WhatsApp na bolha: *negrito*, _itálico_, ~riscado~ e
// ```monoespaçado```. Sem isto a assinatura "*Mikael, disse:*" aparecia com os
// asteriscos crus, diferente do que o cliente vê.
const FORMATO_WHATSAPP = /(```[\s\S]+?```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;

function TextoWhatsApp({ texto }: { texto: string }) {
  const partes = texto.split(FORMATO_WHATSAPP);
  return (
    <>
      {partes.map((p, i) => {
        if (i % 2 === 0) return p;
        if (p.startsWith('```'))
          return (
            <code key={i} className="font-mono text-[0.9em]">
              {p.slice(3, -3)}
            </code>
          );
        const miolo = p.slice(1, -1);
        if (p[0] === '*') return <strong key={i}>{miolo}</strong>;
        if (p[0] === '_') return <em key={i}>{miolo}</em>;
        return <s key={i}>{miolo}</s>;
      })}
    </>
  );
}

export function MessageBubble({
  msg,
  isBot,
  onAbrirMidia,
}: {
  msg: ChatwootMessage;
  isBot: boolean;
  onAbrirMidia: (messageId: number, indice: number) => void;
}) {
  const outgoing = msg.message_type === 1;
  const isPrivate = !!msg.private;
  const attachments = msg.attachments ?? [];
  // Foto e vídeo ocupam a bolha quase inteira (margem fina, como no WhatsApp).
  const temVisual = attachments.some(
    (a) => !!a.data_url && (a.file_type === 'image' || a.file_type === 'video')
  );
  // Quando a Meta recusa, a bolha muda de cor: enviado e não-entregue não podem
  // parecer a mesma coisa — foi assim que um áudio ficou dias "enviado".
  const falha = descreverFalha(msg);
  // A bolha de falha tem fundo claro, então o texto branco do outgoing não vale.
  const emFundoEscuro = outgoing && !isPrivate && !falha;
  if (msg.message_type === 2) {
    return <div className="my-1 text-center text-[11px] text-text-tertiary">{msg.content}</div>;
  }
  return (
    <div className={cn('flex', outgoing ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[74%] rounded-2xl text-sm shadow-sm',
          temVisual ? 'p-1.5' : 'px-3.5 py-2.5',
          isPrivate
            ? 'border border-dashed border-amber-400/60 bg-amber-400/10 text-text-title'
            : falha
              ? 'rounded-br-md border border-error/40 bg-error/10 text-text-title'
              : outgoing
                ? 'rounded-br-md bg-primary text-white'
                : 'rounded-bl-md bg-elevated text-text-title'
        )}
      >
        {isPrivate && (
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-amber-500">
            Nota interna
          </span>
        )}
        {/* O cliente respondeu citando uma mensagem (recurso do WhatsApp). */}
        {msg.in_reply_to && (
          <span
            className={cn(
              'mb-1.5 flex items-start gap-1.5 rounded-lg border-l-2 px-2 py-1 text-xs',
              emFundoEscuro
                ? 'border-white/60 bg-white/10 text-white/85'
                : 'border-primary/50 bg-card text-text-secondary'
            )}
          >
            <CornerDownRight size={12} className="mt-0.5 shrink-0" />
            <span className="line-clamp-2 break-words">
              {msg.in_reply_to.content || 'Mensagem anterior'}
            </span>
          </span>
        )}
        {/* Mídia primeiro, legenda embaixo (igual ao WhatsApp). */}
        {attachments.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {attachments.map((att, i) => (
              <MessageMedia
                key={att.id ?? i}
                att={att}
                outgoing={outgoing && !falha && !isPrivate}
                messageId={msg.id}
                onAbrir={() => onAbrirMidia(msg.id, i)}
              />
            ))}
          </div>
        )}
        {msg.content && (
          <span
            className={cn(
              'block whitespace-pre-wrap break-words leading-relaxed',
              attachments.length > 0 && 'mt-1.5',
              temVisual && 'px-2'
            )}
          >
            <TextoWhatsApp texto={msg.content} />
          </span>
        )}
        {/* Transcrição do áudio, quando o Chatwoot conseguiu gerar. */}
        {attachments.map((att, i) =>
          att.transcribed_text ? (
            <span
              key={`t-${att.id ?? i}`}
              className={cn(
                'mt-1 block text-xs italic leading-relaxed',
                emFundoEscuro ? 'text-white/80' : 'text-text-secondary'
              )}
            >
              “{att.transcribed_text}”
            </span>
          ) : null
        )}
        {/* Mídia do cliente com problema: o motivo vai no title. */}
        {!outgoing && msg.content_attributes?.external_error && attachments.length === 0 && (
          <span
            className="text-xs italic text-text-tertiary"
            title={msg.content_attributes.external_error}
          >
            Mídia indisponível
          </span>
        )}
        {!msg.content && attachments.length === 0 && !msg.content_attributes?.external_error && (
          <span className="text-xs italic text-text-tertiary">Mensagem sem conteúdo</span>
        )}
        {/* O motivo fica na bolha, não só num tooltip: quem está atendendo
            precisa ver o problema sem procurar por ele. O código cru da Meta
            vai no title, para quando alguém for investigar. */}
        {falha && (
          <span
            className="mt-1.5 flex items-start gap-1.5 text-[11px] font-medium leading-snug text-error"
            title={falha.detalhe}
          >
            <AlertTriangle size={13} className="mt-px shrink-0" />
            <span>Não entregue — {falha.resumo}</span>
          </span>
        )}
        <span
          className={cn(
            'mt-1.5 flex items-center justify-end gap-1.5 text-[10px]',
            emFundoEscuro ? 'text-white/70' : 'text-text-tertiary'
          )}
        >
          {isBot && (
            <span
              className={cn(
                'rounded px-1 py-0.5 text-[9px] font-bold uppercase',
                emFundoEscuro ? 'bg-white/20 text-white' : 'bg-primary/15 text-primary'
              )}
            >
              Bot
            </span>
          )}
          {clockTime(msg.created_at)}
        </span>
      </div>
    </div>
  );
}

/**
 * Quanto ainda dá para escrever para este cliente.
 *
 * Fora da janela de 24h o WhatsApp só entrega template aprovado, e o nosso WABA
 * não tem nenhum — na prática, mensagem escrita fora do prazo simplesmente não
 * sai. O chip existe para o atendente ver o relógio antes de gastar a resposta,
 * e não depois.
 */
function ChipJanela({ janela }: { janela: EstadoJanela }) {
  if (janela.estado === 'desconhecida') return null;

  if (janela.estado === 'fechada') {
    return (
      <span
        className="flex shrink-0 items-center gap-1 rounded-lg border border-error/40 bg-error/10 px-2 py-1 text-[11px] font-semibold text-error"
        title={`A janela de 24h fechou há ${duracaoCurta(janela.desdeMs)}. O WhatsApp não entrega mensagem nova até o cliente escrever.`}
      >
        <AlertTriangle size={12} />
        <span className="hidden 2xl:inline">Janela fechada</span>
      </span>
    );
  }

  const urgente = janela.restanteMs <= JANELA_URGENTE_MS;
  return (
    <span
      className={cn(
        'flex shrink-0 items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold',
        urgente
          ? 'border-warning/40 bg-warning/10 text-warning'
          : // Em tela estreita só aparece perto de fechar: falta espaço para o nome.
            'border-border bg-main text-text-secondary max-lg:hidden'
      )}
      title={`Dá para responder livremente até ${janela.fechaEm.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })}. Depois disso o WhatsApp só entrega template aprovado.`}
    >
      <Clock size={12} />
      {duracaoCurta(janela.restanteMs)}
    </span>
  );
}

// Botão do cabeçalho reutilizável.
function HeaderButton({
  onClick,
  disabled,
  variant = 'ghost',
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  variant?: 'ghost' | 'primary' | 'success';
  title: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'success'
          ? 'bg-tertiary text-white hover:opacity-90'
          : variant === 'primary'
            ? 'bg-primary text-white hover:opacity-90'
            : 'border border-border text-text-secondary hover:bg-muted'
      )}
    >
      {children}
    </button>
  );
}

function MenuItem({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-text-title hover:bg-muted"
    >
      {children}
    </button>
  );
}

export function Support() {
  const { manager } = useAuth();
  const isAdmin = !!manager?.roles?.includes('admin');

  const [status, setStatus] = useState('open');
  const [flagFilter, setFlagFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Rascunho POR CONVERSA. Um rascunho só passava de uma conversa para a outra
  // e o texto escrito para um cliente ia parar no outro.
  const [rascunhos, setRascunhos] = useState<Record<number, string>>({});
  const draft = selectedId != null ? (rascunhos[selectedId] ?? '') : '';
  const setDraftDe = useCallback((convId: number, valor: string | ((atual: string) => string)) => {
    setRascunhos((prev) => {
      const atual = prev[convId] ?? '';
      const novo = typeof valor === 'function' ? valor(atual) : valor;
      if (novo === atual) return prev;
      return { ...prev, [convId]: novo };
    });
  }, []);
  const setDraft = useCallback(
    (valor: string | ((atual: string) => string)) => {
      if (selectedId != null) setDraftDe(selectedId, valor);
    },
    [selectedId, setDraftDe]
  );
  // Último envio que falhou: repetir o MESMO texto na MESMA conversa reaproveita
  // a chave, e a API devolve a mensagem em vez de mandar outra vez.
  const envioPendenteRef = useRef<{ convId: number; content: string; id: string } | null>(null);
  // Aviso de erro de qualquer ação (envio, assumir, transferir...). Antes só o
  // 409 aparecia; o resto falhava em silêncio.
  const [aviso, setAviso] = useState<string | null>(null);
  const avisar = useCallback(
    (err: unknown, padrao?: string) => setAviso(mensagemDeErro(err, padrao)),
    []
  );
  useEffect(() => {
    if (!aviso) return;
    const t = window.setTimeout(() => setAviso(null), 8000);
    return () => window.clearTimeout(t);
  }, [aviso]);
  const [isPrivate, setIsPrivate] = useState(false);
  const [gravandoAudio, setGravandoAudio] = useState(false);
  // Arquivo escolhido, aguardando confirmação. Mandar direto no clique é receita
  // de print errado enviado pro cliente.
  const [anexo, setAnexoBruto] = useState<File | null>(null);
  // Editor aberto (foto: seta/rabisco/recorte; vídeo: trecho) e o trecho
  // escolhido do vídeo, que vai junto no envio pro servidor cortar.
  const [editando, setEditando] = useState<'imagem' | 'video' | null>(null);
  const [trechoVideo, setTrechoVideo] = useState<TrechoVideo | null>(null);
  const setAnexo = useCallback((f: File | null) => {
    setAnexoBruto(f);
    setTrechoVideo(null);
    setEditando(null);
  }, []);
  const anexoEhImagem = !!anexo && /^image\/(jpeg|png|webp)$/.test(anexo.type);
  const anexoEhVideo = !!anexo && (anexo.type.startsWith('video/') || /\.mov$/i.test(anexo.name));
  const anexoInputRef = useRef<HTMLInputElement>(null);
  const draftRef = useRef<HTMLTextAreaElement>(null);
  // Altura que o atendente escolheu arrastando o canto do campo. Enquanto
  // existir, o auto-crescimento não encolhe abaixo dela — só cresce se o
  // texto precisar de mais espaço do que isso.
  const alturaManualRef = useRef<number | null>(null);
  // Última altura que O PRÓPRIO auto-crescimento aplicou. O ResizeObserver
  // dispara depois de QUALQUER mudança de altura (a nossa ou um arrasto do
  // atendente); comparar com este valor é como ele diferencia as duas —
  // uma flag resetada por requestAnimationFrame não serve aqui porque o
  // rAF roda ANTES da notificação do ResizeObserver no ciclo do navegador,
  // ou seja, a flag já estaria zerada quando o observer disparasse.
  const ultimaAlturaAutoRef = useRef<number | null>(null);
  // Trocar de conversa fecha o gravador: um áudio gravado aqui não pode ir parar
  // na conversa de outro produtor. A altura arrastada também é por conversa —
  // não faz sentido carregar pra outro atendimento.
  useEffect(() => {
    setGravandoAudio(false);
    // Um arquivo escolhido aqui não pode ir para outro cliente.
    setAnexoBruto(null);
    setTrechoVideo(null);
    setEditando(null);
    setIsPrivate(false);
    setAviso(null);
    alturaManualRef.current = null;
  }, [selectedId]);
  // Prévia do anexo: uma URL por arquivo (não uma por render), criada e
  // liberada no mesmo efeito.
  const [previaAnexo, setPreviaAnexo] = useState<string | null>(null);
  useEffect(() => {
    if (!anexo || !anexo.type.startsWith('image/')) {
      setPreviaAnexo(null);
      return;
    }
    const u = URL.createObjectURL(anexo);
    setPreviaAnexo(u);
    return () => URL.revokeObjectURL(u);
  }, [anexo]);
  // Cresce com o texto (colar, IA, resposta rápida, digitação) até o teto do
  // CSS ou até a altura que o atendente arrastou manualmente — o que for
  // maior; depois disso o próprio textarea rola por dentro.
  useEffect(() => {
    const el = draftRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const alvo = Math.max(el.scrollHeight, alturaManualRef.current ?? 0);
    el.style.height = `${alvo}px`;
    ultimaAlturaAutoRef.current = alvo;
  }, [draft]);
  // Detecta o arrasto do canto (resize nativo do textarea) via ResizeObserver:
  // uma mudança de altura que NÃO bate com o que o efeito acima acabou de
  // aplicar é o atendente redimensionando à mão. Depende de `selectedId`, não
  // de `[]`: o composer só existe no DOM depois de abrir uma conversa, e um
  // efeito sem dependências roda uma única vez, antes disso — o ref ainda
  // estaria nulo e o observer nunca seria (re)anexado.
  useEffect(() => {
    const el = draftRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      const altura = el.offsetHeight;
      if (
        ultimaAlturaAutoRef.current != null &&
        Math.abs(altura - ultimaAlturaAutoRef.current) <= 2
      ) {
        return;
      }
      alturaManualRef.current = altura;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [selectedId]);
  const [menu, setMenu] = useState<null | 'more' | 'transfer' | 'flags' | 'canned' | 'notif'>(null);
  const [msgSearch, setMsgSearch] = useState('');
  const [msgSearchOpen, setMsgSearchOpen] = useState(false);
  // Dados do contato (painel do produtor) por cima da conversa: no tablet e no
  // celular não cabe como coluna fixa, então abre ao tocar no nome.
  const [painelContato, setPainelContato] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Atendente rolou para cima e não está mais vendo o fim da conversa.
  const [longeDoFim, setLongeDoFim] = useState(false);
  // O atendente mexeu no scroll DESTA conversa (roda do mouse ou toque)?
  const rolouManualmenteRef = useRef(false);
  // Wrapper das mensagens — é a altura DELE que o ResizeObserver acompanha.
  const conteudoRef = useRef<HTMLDivElement>(null);
  // Ele clicou numa aba (Abertas/Pendentes/Resolvidas) por conta própria?
  // Enquanto isso valer, o painel não "resgata" a conversa aberta pra aba dela.
  const trocouDeAbaNaMaoRef = useRef(false);
  // Distância até o fim guardada antes de carregar mensagens antigas.
  const restoreOffsetRef = useRef<number | null>(null);

  // Notificações + tempo real vêm do provider GLOBAL do painel (sino no header,
  // banner no canto, som). Aqui a Central consome o mesmo store.
  const {
    connected,
    typing,
    myUserId,
    notifications,
    unreadCount,
    clear,
    markRead,
    alertasPendentes,
    dispensarAlerta,
  } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();

  const convQuery = useConversations('all', status);
  const msgQuery = useMessages(selectedId);
  const sendMutation = useSendMessage();
  const sendAudioMutation = useSendAudio();
  const threadQuery = useThreadHistory(selectedId);
  const sendAttachmentMutation = useSendAttachment();
  const sinalizarDigitando = useTypingSignal(selectedId);
  const toggleMutation = useToggleStatus();
  const assignMutation = useAssign();
  const transferMutation = useTransfer();
  const setLabelsMutation = useSetLabels();
  const pinMutation = usePin();
  const agentsQuery = useAgents();
  const labelsQuery = useLabels();
  const cannedQuery = useCanned();
  const markReadMutation = useMarkConversationRead();
  const acceptTransferMutation = useAcceptTransfer();
  const correctDraftMutation = useCorrectDraft();
  const suggestReplyMutation = useSuggestReply();
  // Transferências já aceitas, por conversa + momento da transferência: se a
  // mesma conversa voltar transferida depois, o modal aparece de novo.
  const [transferenciasAceitas, setTransferenciasAceitas] = useState<Set<string>>(new Set());
  // Encerrar manda a despedida ao cliente e fecha o atendimento — confirma.
  const [confirmarEncerrar, setConfirmarEncerrar] = useState(false);

  // Fio único do contato: os atendimentos anteriores (fatias que o Chatwoot
  // criou antes do `lock_to_single_conversation`) entram ANTES das mensagens da
  // conversa aberta. Como são cronologicamente mais antigos, a ordem continua
  // correta mesmo quando o atendente pagina o trecho atual.
  const messages = useMemo(() => {
    const anteriores = threadQuery.data?.messages ?? [];
    if (!anteriores.length) return msgQuery.messages;
    return [...anteriores, ...msgQuery.messages];
  }, [threadQuery.data, msgQuery.messages]);

  // Quantos atendimentos anteriores existem — vira o aviso no topo da timeline.
  const atendimentosAnteriores = threadQuery.data?.conversations.length ?? 0;

  // Relógio da janela de 24h. Um tick por minuto basta: a informação que importa
  // é "ainda dá" ou "já não dá", e ninguém precisa ver o segundo virar.
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setAgora(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);
  const janela = useMemo(() => estadoDaJanela(messages, agora), [messages, agora]);
  const janelaFechada = janela.estado === 'fechada';

  // Busca dentro da conversa aberta: filtra o que já está carregado. Como o
  // histórico agora pagina, o atendente pode puxar mais e buscar de novo.
  const visibleMessages = useMemo(() => {
    const q = msgSearch.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter((m) => m.content?.toLowerCase().includes(q));
  }, [messages, msgSearch]);

  // Visualizador em tela cheia: navega por todas as fotos, vídeos e PDFs da
  // conversa carregada, na ordem em que aparecem.
  const nomeContato = (convQuery.data?.conversations ?? []).find((c) => c.id === selectedId)?.meta
    ?.sender?.name;
  const galeria = useMemo<ItemMidia[]>(() => {
    const itens: ItemMidia[] = [];
    for (const m of visibleMessages) {
      (m.attachments ?? []).forEach((att, i) => {
        if (!att.data_url) return;
        const info = infoDoArquivo(att);
        if (info.tipo !== 'imagem' && info.tipo !== 'video' && info.tipo !== 'pdf') return;
        itens.push({
          chave: `${m.id}-${i}`,
          messageId: m.id,
          url: att.data_url,
          tipo: info.tipo,
          nome: info.nome,
          legenda: m.content,
          quando: m.created_at,
          deQuem: m.message_type === 1 ? 'Enviado pela Curral Tech' : nomeContato || 'Cliente',
        });
      });
    }
    return itens;
  }, [visibleMessages, nomeContato]);
  const [midiaAberta, setMidiaAberta] = useState<number | null>(null);
  const abrirMidia = useCallback(
    (messageId: number, indice: number) => {
      const pos = galeria.findIndex((g) => g.chave === `${messageId}-${indice}`);
      if (pos >= 0) setMidiaAberta(pos);
    },
    [galeria]
  );
  useEffect(() => setMidiaAberta(null), [selectedId]);

  const agentIds = useMemo(
    () => new Set((agentsQuery.data ?? []).map((a) => a.chatwootUserId)),
    [agentsQuery.data]
  );
  const isBotMsg = useCallback(
    (m: ChatwootMessage) =>
      m.message_type === 1 && !m.private && m.sender?.id != null && !agentIds.has(m.sender.id),
    [agentIds]
  );

  const conversations = useMemo(() => {
    const list = convQuery.data?.conversations ?? [];
    const q = search.trim().toLowerCase();
    return (
      list
        .filter((c) => (flagFilter ? c.labels?.includes(flagFilter) : true))
        .filter((c) => {
          if (!q) return true;
          const name = c.meta?.sender?.name?.toLowerCase() ?? '';
          const phone = c.meta?.sender?.phone_number?.toLowerCase() ?? '';
          return name.includes(q) || phone.includes(q);
        })
        .sort(
          (a, b) =>
            Number(isPinned(b)) - Number(isPinned(a)) || (b.timestamp ?? 0) - (a.timestamp ?? 0)
        )
        // Um contato, uma linha. A inbox rodou sem `lock_to_single_conversation`,
        // então o mesmo cliente pode ter várias conversas — mostrar todas fatia a
        // lista e esconde os outros contatos. Fica a mais recente (a lista já vem
        // ordenada), e o histórico das antigas aparece dentro dela.
        .filter((c, _i, arr) => {
          const contato = c.meta?.sender?.id;
          if (!contato) return true;
          return arr.find((o) => o.meta?.sender?.id === contato) === c;
        })
    );
  }, [convQuery.data, flagFilter, search]);

  // Quantas conversas cada contato tem na lista carregada — vira o selo na
  // linha, pra o atendente saber que ali há mais de um atendimento reunido.
  const fatiasPorContato = useMemo(() => {
    const mapa = new Map<number, number>();
    for (const c of convQuery.data?.conversations ?? []) {
      const id = c.meta?.sender?.id;
      if (id) mapa.set(id, (mapa.get(id) ?? 0) + 1);
    }
    return mapa;
  }, [convQuery.data]);

  const selected = useMemo(
    () => (convQuery.data?.conversations ?? []).find((c) => c.id === selectedId) ?? null,
    [convQuery.data, selectedId]
  );
  const idExibida = selected?.id ?? null;
  const isResolved = selected?.status === 'resolved';
  const assignee = selected?.meta?.assignee ?? null;
  const mine = assignee != null && myUserId != null && assignee.id === myUserId;
  const takenByOther = assignee != null && !mine;
  // Exclusividade absoluta: só o DONO responde/assume (nem admin fura). Admin toma
  // o atendimento via transferência (pra si). Reflete o backstop da API.
  const canReply = mine;
  const pinned = selected ? isPinned(selected) : false;
  const waiting = selected ? waitingMinutes(selected) : null;
  const cat = categoryOf(selected);

  // Transferência pendente PRA MIM: mostra o modal central bloqueante. Só some
  // ao clicar "Assumir" (que dispara a boas-vindas e limpa o marcador no server).
  // Vem da lista da aba atual ou do alerta do provider (que vale em qualquer aba).
  const chaveTransferencia = (c: Conversation) =>
    `${c.id}:${c.custom_attributes?.transfer_at ?? ''}`;
  const pendingTransfer = useMemo(() => {
    if (myUserId == null) return null;
    const found = (convQuery.data?.conversations ?? []).find(
      (c) =>
        c.custom_attributes?.transfer_to === myUserId &&
        !!c.custom_attributes?.transfer_from &&
        !transferenciasAceitas.has(chaveTransferencia(c))
    );
    if (found) {
      return {
        id: found.id,
        de: String(found.custom_attributes?.transfer_from ?? ''),
        nome:
          found.meta?.sender?.name || found.meta?.sender?.phone_number || `Conversa #${found.id}`,
        chave: chaveTransferencia(found),
      };
    }
    const alerta = alertasPendentes.find((a) => a.kind === 'transfer');
    return alerta
      ? { id: alerta.convId, de: '', nome: alerta.title, chave: `${alerta.convId}:alerta` }
      : null;
  }, [convQuery.data, myUserId, transferenciasAceitas, alertasPendentes]);

  const statusText = isResolved
    ? 'Resolvida'
    : waiting != null
      ? 'Aguardando resposta'
      : assignee
        ? `Em atendimento${mine ? ' (você)' : ` · ${assignee.name}`}`
        : 'Aberta';

  const statusNoun = STATUS_FILTERS.find((s) => s.key === status)?.noun ?? 'aberta';
  const countLabel = `${conversations.length} ${statusNoun}${conversations.length === 1 ? '' : 's'}`;

  // Carrega o trecho anterior guardando a distância até o fim, pra devolver o
  // scroll exatamente onde o atendente estava lendo (senão a lista "pula").
  const loadOlder = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !msgQuery.hasOlder || msgQuery.isLoadingOlder) return;
    restoreOffsetRef.current = el.scrollHeight - el.scrollTop;
    void msgQuery.loadOlder();
  }, [msgQuery]);

  // A partir de quantos pixels do fim consideramos que o atendente "saiu" da
  // ponta da conversa — e paramos de arrastá-lo para baixo a cada mensagem.
  const LONGE_DO_FIM_PX = 240;

  const irParaMaisRecente = useCallback((suave = true) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: suave ? 'smooth' : 'auto' });
    setLongeDoFim(false);
  }, []);

  // Abrir uma conversa cai direto na última mensagem, sem animação: `smooth`
  // aqui fazia a conversa rolar do topo até o fim na frente do atendente.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    rolouManualmenteRef.current = false;
    el.scrollTop = el.scrollHeight;
    setLongeDoFim(false);
    // Depende da conversa EXIBIDA, não só da escolhida: vinda por link, ela
    // aparece depois (o painel acha a aba dela), e aí a área de rolagem nasce
    // agora. Com [selectedId] a conversa abria no topo do histórico.
  }, [idExibida]);

  // Mantém a conversa colada no fim enquanto o atendente não rolou.
  //
  // Observa a ALTURA do conteúdo, não as mutações do DOM. É a diferença que
  // importa: MutationObserver dispara a cada render (e foi o que fazia a
  // conversa saltar a cada mudança de status), enquanto o ResizeObserver só
  // acorda quando o conteúdo realmente cresce — imagem que carregou, áudio que
  // ganhou altura, nota da IA que chegou, histórico que veio depois. O ajuste é
  // instantâneo (sem `smooth`), então não se vê rolagem: a vista simplesmente
  // continua no fim.
  useEffect(() => {
    const el = scrollRef.current;
    const conteudo = conteudoRef.current;
    if (!el || !conteudo) return;
    const observer = new ResizeObserver(() => {
      if (rolouManualmenteRef.current) return;
      el.scrollTop = el.scrollHeight;
      setLongeDoFim(false);
    });
    observer.observe(conteudo);
    return () => observer.disconnect();
  }, [idExibida]);

  // Só a mão do atendente conta como "saí do fim".
  //
  // Sem isto, conversa com histórico unificado abria no meio: as mensagens dos
  // atendimentos anteriores chegam DEPOIS e entram ACIMA das atuais; o browser
  // preserva o scrollTop, a distância até o fim explode, e o código concluía
  // que o atendente tinha subido — quando ele não encostou no mouse.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const marcar = () => {
      rolouManualmenteRef.current = true;
    };
    el.addEventListener('wheel', marcar, { passive: true });
    el.addEventListener('touchmove', marcar, { passive: true });
    return () => {
      el.removeEventListener('wheel', marcar);
      el.removeEventListener('touchmove', marcar);
    };
  }, [idExibida]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Veio trecho antigo: devolve o scroll onde ele estava lendo.
    if (restoreOffsetRef.current != null) {
      el.scrollTop = el.scrollHeight - restoreOffsetRef.current;
      restoreOffsetRef.current = null;
      return;
    }
    // Enquanto o atendente não rolou nada, a conversa fica ancorada no fim —
    // é o caso do histórico unificado, que chega depois e entra por cima.
    if (!rolouManualmenteRef.current) {
      el.scrollTop = el.scrollHeight;
      setLongeDoFim(false);
      return;
    }
    // Depois que ele rolou: mensagem nova só puxa para baixo quem já estava na
    // ponta. Quem subiu para reler continua onde está, e o botão avisa.
    const distancia = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distancia <= LONGE_DO_FIM_PX) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages.length]);

  useEffect(() => {
    setMenu(null);
    setMsgSearch('');
    setMsgSearchOpen(false);
    setPainelContato(false);
    setConfirmarEncerrar(false); // trocou de conversa: some a confirmação
  }, [selectedId]);

  // App nativo: a conversa aberta não gera push neste aparelho.
  useEffect(() => {
    informarConversaAtiva(selectedId ?? null);
  }, [selectedId]);
  useEffect(() => () => informarConversaAtiva(null), []);

  // A conversa aberta sai da aba quando muda de status — você assume uma
  // pendente e ela vira aberta, ou o bot entrega e ela sai de pendentes. Antes
  // ela simplesmente sumia da tela e era preciso ir na outra aba e clicar de
  // novo, no meio do atendimento. Agora o painel troca de aba sozinho e a
  // conversa continua onde estava.
  useEffect(() => {
    if (!selectedId || convQuery.isLoading || convQuery.isFetching) return;
    // Você trocou de aba de propósito: não é hora de "resgatar" nada. Sem esta
    // guarda o painel devolvia você pra aba da conversa aberta e ficava
    // impossível navegar entre Abertas/Pendentes/Resolvidas.
    if (trocouDeAbaNaMaoRef.current) return;
    const estaNaAbaAtual = (convQuery.data?.conversations ?? []).some((c) => c.id === selectedId);
    if (estaNaAbaAtual) return;

    let cancelado = false;
    (async () => {
      for (const outra of STATUS_FILTERS.map((f) => f.key).filter((k) => k !== status)) {
        try {
          const r = await supportApi.listConversations({ status: outra, assignee_type: 'all' });
          if (cancelado) return;
          if (r.conversations.some((c) => c.id === selectedId)) {
            setStatus(outra);
            return;
          }
        } catch {
          // aba indisponível não impede tentar a próxima
        }
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [selectedId, convQuery.data, convQuery.isLoading, convQuery.isFetching, status]);

  // Abre a conversa vinda de uma notificação (?conv=ID) e marca como lida.
  // O parâmetro é consumido e apagado: senão, abrir a mesma conversa pelo
  // banner uma segunda vez não mudava a URL e nada acontecia.
  useEffect(() => {
    const c = searchParams.get('conv');
    if (!c) return;
    const id = Number(c);
    if (!Number.isNaN(id)) {
      trocouDeAbaNaMaoRef.current = false;
      setSelectedId(id);
      markRead(id);
      markReadMutation.mutate(id);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('conv');
        return next;
      },
      { replace: true }
    );
    // markReadMutation.mutate é estável no react-query v5.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, markRead]);

  const handleSend = (e: FormEvent) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || selectedId == null) return;
    // O Enter não passa pelo botão desabilitado — sem esta guarda dava pra
    // mandar a mensagem no vazio do mesmo jeito. Nota interna não sai daqui,
    // então não depende da janela.
    if (janelaFechada && !isPrivate) return;
    const convId = selectedId;
    const pendente = envioPendenteRef.current;
    const clientMessageId =
      pendente && pendente.convId === convId && pendente.content === content
        ? pendente.id
        : novoIdDeEnvio();
    envioPendenteRef.current = { convId, content, id: clientMessageId };
    // O campo esvazia NA HORA: um segundo Enter não tem o que mandar, e o que
    // o atendente começar a escrever enquanto envia não é apagado depois.
    setDraftDe(convId, '');
    setAviso(null);
    sendMutation.mutate(
      { convId, content, isPrivate, clientMessageId },
      {
        onSuccess: () => {
          envioPendenteRef.current = null;
        },
        onError: (err) => {
          // Devolve o texto (se o campo continua vazio) pro atendente decidir.
          setDraftDe(convId, (atual) => atual || content);
          if (codigoDeErro(err) !== 'WINDOW_CLOSED')
            avisar(err, 'Não foi possível enviar a mensagem.');
          else avisar(err);
        },
      }
    );
  };

  const openConversation = (id: number) => {
    // Escolheu uma conversa: o painel volta a poder segui-la quando ela mudar
    // de status.
    trocouDeAbaNaMaoRef.current = false;
    setSelectedId(id);
    markRead(id); // limpa a notificação no sino
    markReadMutation.mutate(id); // zera o unread_count no Chatwoot
  };

  // Aceitar a transferência: assume no server (boas-vindas + limpa o marcador)
  // e abre a conversa. Se falhar, o modal volta e o motivo aparece.
  const acceptTransfer = (t: { id: number; chave: string }) => {
    setTransferenciasAceitas((prev) => new Set(prev).add(t.chave));
    acceptTransferMutation.mutate(t.id, {
      onSuccess: () => {
        dispensarAlerta(t.id);
        openConversation(t.id);
      },
      onError: (err) => {
        setTransferenciasAceitas((prev) => {
          const next = new Set(prev);
          next.delete(t.chave);
          return next;
        });
        avisar(err, 'Não foi possível assumir a transferência.');
      },
    });
  };

  const toggleLabel = (slug: string) => {
    if (!selected) return;
    const curr = selected.labels ?? [];
    const next = curr.includes(slug) ? curr.filter((l) => l !== slug) : [...curr, slug];
    setLabelsMutation.mutate(
      { convId: selected.id, labels: next },
      { onError: (err) => avisar(err, 'Não foi possível salvar as etiquetas.') }
    );
  };

  // Assumir/largar sempre com o id explícito da conversa.
  const assumir = (convId: number, unassign = false) =>
    assignMutation.mutate(
      { convId, unassign },
      {
        onSuccess: () => {
          if (!unassign) dispensarAlerta(convId);
        },
        onError: (err) =>
          avisar(
            err,
            unassign
              ? 'Não foi possível largar a conversa.'
              : 'Não foi possível assumir a conversa.'
          ),
      }
    );

  const mudarStatus = (convId: number, novo: 'open' | 'resolved' | 'pending' | 'snoozed') =>
    toggleMutation.mutate(
      { convId, status: novo },
      { onError: (err) => avisar(err, 'Não foi possível mudar o status da conversa.') }
    );

  const unavailable =
    convQuery.isError &&
    (convQuery.error as { response?: { status?: number } })?.response?.status === 503;

  const otherAgents = (agentsQuery.data ?? []).filter((a) => a.chatwootUserId !== myUserId);

  // Pop-up "Novo atendimento aguardando": vem do alerta do provider, que some
  // em todas as telas quando QUALQUER atendente assume. O som é do provider
  // também (um só, que para junto).
  const triageAlert = useMemo(() => {
    const alerta = alertasPendentes.find((a) => a.kind === 'ready');
    if (!alerta) return null;
    const naLista = (convQuery.data?.conversations ?? []).find((c) => c.id === alerta.convId);
    if (naLista?.meta?.assignee) return null;
    return alerta;
  }, [alertasPendentes, convQuery.data]);
  // Se a lista já mostra dono, o alerta para (caso o evento tenha se perdido).
  useEffect(() => {
    for (const a of alertasPendentes) {
      const c = (convQuery.data?.conversations ?? []).find((x) => x.id === a.convId);
      if (a.kind === 'ready' && c?.meta?.assignee) dispensarAlerta(a.convId);
    }
  }, [alertasPendentes, convQuery.data, dispensarAlerta]);

  // Mensagem nova chegando com a conversa aberta e a aba visível: já conta
  // como lida (o contador da lista subia com o atendente olhando).
  const naoLidasAbertas = selected?.unread_count ?? 0;
  useEffect(() => {
    if (!selected || naoLidasAbertas === 0 || document.visibilityState !== 'visible') return;
    markReadMutation.mutate(selected.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, naoLidasAbertas]);

  return (
    <Layout
      breadcrumbs={[{ label: 'Suporte ao Cliente' }]}
      hideHeader
      hideBottomNav={selectedId != null}
      recolherMenu
    >
      {/* Altura da tela menos a barra inferior do app (quando aparece) e com
          o safe-area do topo (notch) no app. */}
      <div className="relative flex h-[calc(100dvh-var(--app-bottom,0px))] overflow-hidden bg-card pt-[env(safe-area-inset-top)]">
        {menu && <div className="fixed inset-0 z-10" onClick={() => setMenu(null)} />}

        {/* Controles flutuantes (sem barra de topo) */}
        {/* No celular, com a conversa aberta, este botão cairia em cima do
            "voltar" — e lá o menu lateral já é alcançável pela lista. */}
        <SidebarTrigger
          className={cn(
            'absolute left-3 top-3 z-40 size-9 rounded-lg bg-card/70 text-text-secondary shadow-sm ring-1 ring-border backdrop-blur transition-colors hover:bg-muted hover:text-text-title md:flex',
            selectedId ? 'hidden' : 'flex'
          )}
        />
        {/* Mesmo motivo do botão do menu: no celular, com a conversa aberta,
            este sino comia o espaço do nome do cliente no cabeçalho. Fica na
            lista, que é onde ele serve. */}
        <div
          className={cn('absolute right-4 top-3.5 z-40 md:block', selectedId ? 'hidden' : 'block')}
        >
          <button
            type="button"
            onClick={() => setMenu(menu === 'notif' ? null : 'notif')}
            title="Notificações"
            className="relative flex size-9 items-center justify-center rounded-lg bg-card/70 text-text-secondary shadow-sm ring-1 ring-border backdrop-blur transition-colors hover:bg-muted hover:text-text-title"
          >
            <Bell size={17} />
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-secondary text-[9px] font-bold text-white">
                {unreadCount}
              </span>
            )}
          </button>
          {menu === 'notif' && (
            <div className="absolute right-0 top-11 z-40 w-72 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
              <div className="flex items-center justify-between border-b border-border px-3 py-2">
                <span className="text-xs font-semibold text-text-title">Notificações</span>
                {notifications.length > 0 && (
                  <button
                    type="button"
                    className="text-[11px] text-primary hover:underline"
                    onClick={clear}
                  >
                    Limpar
                  </button>
                )}
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="px-3 py-6 text-center text-xs text-text-tertiary">
                    Sem notificações.
                  </p>
                ) : (
                  notifications.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => {
                        openConversation(n.convId);
                        setMenu(null);
                      }}
                      className={cn(
                        'flex w-full items-start gap-2 border-b border-border px-3 py-2 text-left hover:bg-muted',
                        !n.read && 'bg-primary/5'
                      )}
                    >
                      <span className="mt-0.5 text-tertiary">
                        <MessageSquare size={14} />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-xs font-medium text-text-title">
                          {n.title}
                        </span>
                        <span className="text-[11px] text-text-secondary">
                          {n.kind === 'ready'
                            ? 'Pronto para atendimento'
                            : 'Nova conversa iniciada'}
                        </span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* ─────────── Lista ─────────── */}
        {/* Lista. No celular ela é a tela inteira, e some quando uma conversa
            é aberta — os dois lado a lado espremiam a conversa em ~70px. A
            partir de md volta a ser a coluna fixa de sempre. */}
        <aside
          className={cn(
            'w-full shrink-0 flex-col border-r border-border md:flex md:max-w-[20rem] lg:max-w-[22rem] xl:max-w-[24rem] 2xl:max-w-[28rem]',
            selectedId ? 'hidden' : 'flex'
          )}
        >
          <div className="flex items-center justify-between gap-2 pb-2 pl-14 pr-4 pt-4">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-text-title">Suporte</h2>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                {countLabel}
              </span>
            </div>
            <span title={connected ? 'Tempo real ativo' : 'Reconectando…'}>
              <span
                className={cn(
                  'block size-2 rounded-full',
                  connected ? 'animate-pulse bg-tertiary' : 'bg-text-tertiary'
                )}
              />
            </span>
          </div>

          {/* Busca */}
          <div className="px-4 pb-2 pt-2">
            <div className="flex items-center gap-2 rounded-xl bg-main px-3 py-2">
              <Search size={15} className="text-text-tertiary" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar nome ou telefone"
                className="w-full bg-transparent text-xs text-text-title outline-none placeholder:text-text-tertiary"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-text-tertiary hover:text-text-title"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-1 px-4 pb-2">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => {
                  trocouDeAbaNaMaoRef.current = true;
                  setStatus(s.key);
                }}
                className={cn(
                  'flex-1 whitespace-nowrap rounded-lg px-1.5 py-1.5 text-xs font-semibold transition-colors xl:px-2',
                  status === s.key ? 'bg-primary text-white' : 'text-text-secondary hover:bg-muted'
                )}
              >
                <span className="xl:hidden">{s.curto ?? s.label}</span>
                <span className="max-xl:hidden">{s.label}</span>
              </button>
            ))}
          </div>

          {/* Flags */}
          <div className="flex items-center gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pb-3">
            <button
              type="button"
              onClick={() => setFlagFilter(null)}
              className={cn(
                'shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium',
                flagFilter === null
                  ? 'bg-elevated text-text-title'
                  : 'text-text-tertiary hover:bg-muted'
              )}
            >
              Todas
            </button>
            {FILTER_FLAGS.map((f) => {
              const m = flagMeta(f);
              const on = flagFilter === f;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFlagFilter(on ? null : f)}
                  className={cn(
                    'flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium',
                    on ? m.cls : 'text-text-tertiary hover:bg-muted'
                  )}
                >
                  <span className={cn('size-1.5 rounded-full', m.dot)} />
                  {m.label.replace('Suporte ', '').replace(' usuário', '')}
                </button>
              );
            })}
          </div>

          <div className="flex-1 space-y-1 overflow-y-auto px-2 pb-2">
            {convQuery.isLoading ? (
              <div className="flex h-full items-center justify-center text-text-tertiary">
                <Loader2 className="animate-spin" size={20} />
              </div>
            ) : unavailable ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-text-tertiary">
                <WifiOff size={22} />
                <p className="text-xs">Suporte ainda não configurado.</p>
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-text-tertiary">
                <MessageSquare size={22} />
                <p className="text-xs">
                  {search || flagFilter ? 'Nada encontrado.' : 'Nenhuma conversa por aqui.'}
                </p>
              </div>
            ) : (
              <>
                {conversations.map((c) => (
                  <ConversationRow
                    key={c.id}
                    conv={c}
                    active={c.id === selectedId}
                    typing={typing.has(c.id)}
                    reunidas={fatiasPorContato.get(c.meta?.sender?.id ?? -1) ?? 1}
                    onClick={() => openConversation(c.id)}
                  />
                ))}
                <p className="py-3 text-center text-[11px] text-text-tertiary">
                  Fim das conversas{' '}
                  {STATUS_FILTERS.find((s) => s.key === status)?.label.toLowerCase()}
                </p>
              </>
            )}
          </div>
        </aside>

        {/* ─────────── Thread ─────────── */}
        {/* Conversa. No celular só aparece depois de escolher alguém na lista. */}
        <section
          className={cn('min-w-0 flex-1 flex-col bg-main md:flex', selectedId ? 'flex' : 'hidden')}
        >
          {selected ? (
            <>
              <header className="flex items-center gap-2 border-b border-border bg-card px-3 py-3 sm:gap-3 sm:px-4">
                {/* Sem isto não há como voltar à lista no celular: lá a
                    conversa ocupa a tela toda. */}
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  aria-label="Voltar para a lista"
                  className="-ml-1 flex size-8 shrink-0 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-muted hover:text-text-title md:hidden"
                >
                  <ChevronLeft size={20} />
                </button>
                {/* Tocar no contato abre os dados dele (abaixo de 1360px, onde o
                    painel do produtor não fica fixo ao lado). */}
                <button
                  type="button"
                  onClick={() => setPainelContato(true)}
                  title="Dados do contato"
                  className={cn(
                    'flex min-w-0 flex-1 items-center gap-2 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 sm:gap-3 min-[1360px]:pointer-events-none',
                    msgSearchOpen && 'max-md:flex-none'
                  )}
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-tertiary/15 text-sm font-semibold text-tertiary">
                    {initials(selected.meta?.sender?.name)}
                  </span>
                  {/* No celular a busca aberta ocupa o lugar do nome. */}
                  <div className={cn('min-w-0 flex-1', msgSearchOpen && 'max-md:hidden')}>
                    <p className="truncate text-sm font-bold text-text-title">
                      {selected.meta?.sender?.name || `Conversa #${selected.id}`}
                    </p>
                    {typing.has(selected.id) ? (
                      <p className="text-xs font-medium text-tertiary">digitando…</p>
                    ) : (
                      // `truncate` aqui porque "Em atendimento (você)" quebrava em
                      // três linhas e esticava o cabeçalho no celular.
                      <p className="flex items-center gap-1.5 truncate text-xs text-text-secondary">
                        <span
                          className={cn(
                            'size-1.5 rounded-full',
                            isResolved ? 'bg-text-tertiary' : 'bg-tertiary'
                          )}
                        />
                        {statusText}
                        {cat && <span className="text-text-tertiary">· {flagMeta(cat).label}</span>}
                      </p>
                    )}
                  </div>
                </button>

                {/* O sino de notificações é `absolute` no canto — sem esta
                    margem os botões passavam por baixo dele no celular. */}
                <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2 md:pr-11 min-[1360px]:pr-0">
                  <ChipJanela janela={janela} />

                  {/* Busca dentro da conversa */}
                  {msgSearchOpen ? (
                    <div className="flex items-center gap-1 rounded-lg border border-border bg-main px-2 py-1">
                      <Search size={13} className="shrink-0 text-text-tertiary" />
                      <input
                        autoFocus
                        value={msgSearch}
                        onChange={(e) => setMsgSearch(e.target.value)}
                        placeholder="Buscar na conversa"
                        aria-label="Buscar na conversa"
                        className="w-36 bg-transparent text-xs text-text-title outline-none placeholder:text-text-tertiary"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setMsgSearch('');
                          setMsgSearchOpen(false);
                        }}
                        aria-label="Fechar busca na conversa"
                        className="text-text-tertiary hover:text-text-title"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    // Abaixo de 1024px a busca fica no menu "...": sem espaço para o nome.
                    <span className="max-lg:hidden">
                      <HeaderButton
                        onClick={() => setMsgSearchOpen(true)}
                        title="Buscar na conversa"
                      >
                        <Search size={14} />
                        <span className="hidden 2xl:inline">Buscar</span>
                      </HeaderButton>
                    </span>
                  )}

                  {/* Dados do contato entre 1024 e 1360px (abaixo disso estão no
                      "..." e no toque no nome; acima, fixos ao lado). */}
                  <span className="hidden lg:inline-flex min-[1360px]:hidden">
                    <HeaderButton onClick={() => setPainelContato(true)} title="Dados do contato">
                      <PanelRight size={14} />
                    </HeaderButton>
                  </span>

                  {/* Assumir / Largar — conversa encerrada não se assume (reabra antes). */}
                  {!isResolved && (
                    <HeaderButton
                      onClick={() => assumir(selected.id, mine)}
                      disabled={assignMutation.isPending || takenByOther}
                      title={
                        takenByOther
                          ? `Em atendimento por ${assignee?.name} — transfira para assumir`
                          : mine
                            ? 'Largar'
                            : 'Assumir'
                      }
                    >
                      {assignMutation.isPending ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : mine ? (
                        <UserMinus size={14} />
                      ) : (
                        <UserPlus size={14} />
                      )}
                      {/* No celular fica só o ícone: os rótulos empurravam os
                        botões pra fora da tela. O `title` mantém a dica. */}
                      <span className="hidden 2xl:inline">{mine ? 'Largar' : 'Assumir'}</span>
                    </HeaderButton>
                  )}

                  {/* Resolver / Reabrir (destaque). Encerrar avisa o cliente e
                      fecha o atendimento — passa por confirmação. Reabrir não:
                      é reversível e não manda nada pra ninguém. */}
                  <HeaderButton
                    onClick={() =>
                      isResolved ? mudarStatus(selected.id, 'open') : setConfirmarEncerrar(true)
                    }
                    disabled={toggleMutation.isPending}
                    variant="success"
                    title={isResolved ? 'Reabrir' : 'Resolver'}
                  >
                    {toggleMutation.isPending ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : isResolved ? (
                      <RotateCcw size={14} />
                    ) : (
                      <CheckCircle2 size={14} />
                    )}
                    <span className="hidden 2xl:inline">{isResolved ? 'Reabrir' : 'Resolver'}</span>
                  </HeaderButton>

                  {/* Menu "..." com o resto das ações */}
                  <div className="relative z-20">
                    <button
                      type="button"
                      onClick={() =>
                        setMenu(
                          menu === 'more' || menu === 'transfer' || menu === 'flags' ? null : 'more'
                        )
                      }
                      title="Mais ações"
                      className="rounded-lg border border-border p-2 text-text-secondary hover:bg-muted"
                    >
                      <MoreHorizontal size={16} />
                    </button>

                    {menu === 'more' && (
                      <div className="absolute right-0 top-11 z-20 w-52 overflow-hidden rounded-xl border border-border bg-card py-1 shadow-xl">
                        <div className="lg:hidden">
                          <MenuItem
                            onClick={() => {
                              setPainelContato(true);
                              setMenu(null);
                            }}
                          >
                            <PanelRight size={14} className="text-text-secondary" />
                            Dados do contato
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              setMsgSearchOpen(true);
                              setMenu(null);
                            }}
                          >
                            <Search size={14} className="text-text-secondary" />
                            Buscar na conversa
                          </MenuItem>
                        </div>
                        <MenuItem
                          onClick={() => {
                            pinMutation.mutate(
                              { convId: selected.id, pinned: !pinned },
                              {
                                onError: (err) => avisar(err, 'Não foi possível fixar a conversa.'),
                              }
                            );
                            setMenu(null);
                          }}
                        >
                          <Star
                            size={14}
                            className={pinned ? 'text-amber-500' : 'text-text-tertiary'}
                            fill={pinned ? 'currentColor' : 'none'}
                          />
                          {pinned ? 'Desafixar' : 'Fixar no topo'}
                        </MenuItem>
                        {(mine || isAdmin) && (
                          <MenuItem onClick={() => setMenu('transfer')}>
                            <Forward size={14} className="text-text-tertiary" /> Transferir…
                          </MenuItem>
                        )}
                        <MenuItem onClick={() => setMenu('flags')}>
                          <Tag size={14} className="text-text-tertiary" /> Etiquetas…
                        </MenuItem>
                        {!isResolved && (
                          <MenuItem
                            onClick={() => {
                              mudarStatus(
                                selected.id,
                                selected.status === 'snoozed' ? 'open' : 'snoozed'
                              );
                              setMenu(null);
                            }}
                          >
                            <Pause size={14} className="text-text-tertiary" />
                            {selected.status === 'snoozed' ? 'Tirar da pausa' : 'Pausar'}
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={() => {
                            msgQuery.refetch();
                            setMenu(null);
                          }}
                        >
                          <RefreshCw size={14} className="text-text-tertiary" /> Atualizar
                        </MenuItem>
                      </div>
                    )}

                    {menu === 'transfer' && (
                      <div className="absolute right-0 top-11 z-20 w-60 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
                        <button
                          type="button"
                          onClick={() => setMenu('more')}
                          className="flex w-full items-center gap-1 border-b border-border px-3 py-2 text-xs font-semibold text-text-title hover:bg-muted"
                        >
                          <ChevronLeft size={14} /> Transferir para
                        </button>
                        <div className="max-h-64 overflow-y-auto py-1">
                          {otherAgents.map((a) => (
                            <MenuItem
                              key={a.chatwootUserId}
                              onClick={() => {
                                transferMutation.mutate(
                                  { convId: selected.id, assigneeId: a.chatwootUserId },
                                  { onError: (err) => avisar(err, 'Não foi possível transferir.') }
                                );
                                setMenu(null);
                              }}
                            >
                              <span className="flex size-6 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
                                {initials(a.name)}
                              </span>
                              {a.name}
                            </MenuItem>
                          ))}
                          {otherAgents.length === 0 && (
                            <p className="px-3 py-3 text-center text-xs text-text-tertiary">
                              Sem outros atendentes.
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {menu === 'flags' && (
                      <div className="absolute right-0 top-11 z-20 w-60 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
                        <button
                          type="button"
                          onClick={() => setMenu('more')}
                          className="flex w-full items-center gap-1 border-b border-border px-3 py-2 text-xs font-semibold text-text-title hover:bg-muted"
                        >
                          <ChevronLeft size={14} /> Etiquetas
                        </button>
                        <div className="max-h-64 overflow-y-auto py-1">
                          {(labelsQuery.data ?? []).map((lb) => {
                            const on = selected.labels?.includes(lb.title);
                            const m = flagMeta(lb.title);
                            return (
                              <button
                                key={lb.id}
                                type="button"
                                onClick={() => toggleLabel(lb.title)}
                                className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted"
                              >
                                <span className="flex items-center gap-2">
                                  <span className={cn('size-2 rounded-full', m.dot)} />
                                  <span className="text-text-title">{m.label}</span>
                                </span>
                                {on && <CheckCircle2 size={14} className="text-tertiary" />}
                              </button>
                            );
                          })}
                          {(labelsQuery.data ?? []).length === 0 && (
                            <p className="px-3 py-3 text-center text-xs text-text-tertiary">
                              Sem etiquetas.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </header>

              {/* Etiquetas ativas */}
              {selected.labels?.length ? (
                <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-card/50 px-4 py-2">
                  <FlagBadges labels={selected.labels} />
                </div>
              ) : null}

              <div
                ref={scrollRef}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  // `rolouManualmente` protege o carregamento automático: com a
                  // conversa ancorada no fim e pouco conteúdo, scrollTop fica em
                  // 0 e as mensagens antigas viriam em cascata sozinhas.
                  if (rolouManualmenteRef.current && el.scrollTop < 80 && !msgSearch) loadOlder();
                  // Já o BOTÃO é pura geometria: se não estou vendo a última
                  // mensagem, ele aparece — não importa como cheguei aqui.
                  // Amarrá-lo à rolagem manual escondia a saída justamente
                  // quando ela era necessária (conteúdo que empurrou a vista).
                  const distancia = el.scrollHeight - el.scrollTop - el.clientHeight;
                  // Voltou pro fim por conta própria: a conversa volta a se
                  // manter colada lá. Sem isto, uma rolagem no começo do
                  // atendimento desligava a âncora até trocar de conversa.
                  if (distancia <= 40) rolouManualmenteRef.current = false;
                  setLongeDoFim(distancia > LONGE_DO_FIM_PX);
                }}
                className="chat-scroll relative flex-1 overflow-y-auto px-4 py-4"
              >
                <div ref={conteudoRef} className="space-y-1.5">
                  {/* Topo do histórico: puxa o trecho anterior (inclui a nota de triagem). */}
                  {!msgQuery.isLoading && !msgSearch && msgQuery.hasOlder && (
                    <div className="flex justify-center pb-2">
                      <button
                        type="button"
                        onClick={loadOlder}
                        disabled={msgQuery.isLoadingOlder}
                        className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[11px] font-semibold text-text-secondary transition-colors hover:bg-muted disabled:opacity-60"
                      >
                        {msgQuery.isLoadingOlder ? (
                          <>
                            <Loader2 className="animate-spin" size={11} /> Carregando…
                          </>
                        ) : (
                          'Carregar mensagens anteriores'
                        )}
                      </button>
                    </div>
                  )}
                  {!msgQuery.isLoading &&
                    !msgQuery.hasOlder &&
                    messages.length > 0 &&
                    !msgSearch && (
                      <p className="pb-2 text-center text-[11px] text-text-tertiary">
                        {atendimentosAnteriores > 0
                          ? `Início do histórico deste contato — ${atendimentosAnteriores + 1} atendimentos reunidos`
                          : 'Início da conversa'}
                      </p>
                    )}
                  {msgSearch && (
                    <p className="pb-2 text-center text-[11px] text-text-tertiary">
                      {visibleMessages.length === 0
                        ? 'Nenhuma mensagem encontrada no trecho carregado.'
                        : `${visibleMessages.length} mensagem${visibleMessages.length === 1 ? '' : 's'} com "${msgSearch}"`}
                    </p>
                  )}
                  {msgQuery.isLoading ? (
                    <div className="flex h-full items-center justify-center text-text-tertiary">
                      <Loader2 className="animate-spin" size={20} />
                    </div>
                  ) : (
                    visibleMessages.map((m, i, arr) => {
                      const prev = arr[i - 1];
                      const showDay = !prev || dayLabel(prev.created_at) !== dayLabel(m.created_at);
                      // Fronteira entre atendimentos: cada fatia antiga tem seu
                      // thread_conversation_id; a conversa aberta não tem nenhum.
                      const fatia = (m as { thread_conversation_id?: number })
                        .thread_conversation_id;
                      const fatiaAnterior = prev
                        ? (prev as { thread_conversation_id?: number }).thread_conversation_id
                        : undefined;
                      const trocouAtendimento = !!prev && fatia !== fatiaAnterior;
                      // Sessão nova: a mensagem anterior foi um encerramento, logo
                      // o que vem daqui pra baixo é OUTRO chamado. Vale também
                      // dentro da mesma conversa, que é o caso de hoje.
                      const novaSessao = !!prev && ehEncerramento(prev) && !trocouAtendimento;
                      return (
                        <div key={`${fatia ?? 'atual'}-${m.id}`}>
                          {novaSessao && (
                            <div className="my-4 flex items-center gap-2">
                              <span className="h-px flex-1 bg-tertiary/40" />
                              <span className="rounded-full bg-tertiary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-tertiary">
                                Novo atendimento
                              </span>
                              <span className="h-px flex-1 bg-tertiary/40" />
                            </div>
                          )}
                          {trocouAtendimento && (
                            <div className="my-4 flex items-center gap-2">
                              <span className="h-px flex-1 bg-border" />
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
                                {fatia ? 'Atendimento anterior' : 'Atendimento atual'}
                              </span>
                              <span className="h-px flex-1 bg-border" />
                            </div>
                          )}
                          {showDay && (
                            <div className="my-3 flex items-center justify-center">
                              <span className="rounded-full bg-elevated px-3 py-1 text-[11px] font-medium text-text-tertiary">
                                {dayLabel(m.created_at)}
                              </span>
                            </div>
                          )}
                          <MessageBubble msg={m} isBot={isBotMsg(m)} onAbrirMidia={abrirMidia} />
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Volta pro fim da conversa. `sticky` dentro da própria área
                    de rolagem: assim ele acompanha a base do que está visível
                    sem depender da altura do composer, que muda com anexo e
                    gravação. Altura zero pra não empurrar as mensagens. */}
                {longeDoFim && !msgSearch && (
                  <div className="sticky bottom-0 z-10 flex h-0 justify-end pr-1">
                    <button
                      type="button"
                      onClick={() => irParaMaisRecente()}
                      aria-label="Ir para a mensagem mais recente"
                      title="Ir para a mensagem mais recente"
                      className="-translate-y-10 flex size-9 items-center justify-center rounded-full border border-border bg-card text-text-secondary shadow-lg transition-colors hover:bg-muted hover:text-text-title"
                    >
                      <ChevronDown size={18} />
                    </button>
                  </div>
                )}
              </div>

              {/* Composer */}
              {canReply ? (
                <form onSubmit={handleSend} className="border-t border-border bg-card p-3">
                  {/* Avisar ANTES de escrever. Um atendimento inteiro já foi
                      digitado depois do prazo — seis mensagens, nenhuma
                      entregue, e nada na tela dizendo isso. Nota interna não
                      passa pelo WhatsApp, então segue liberada. */}
                  {janelaFechada && !isPrivate && (
                    <div className="mb-2 flex items-start gap-2 rounded-lg border border-error/40 bg-error/10 px-3 py-2">
                      <AlertTriangle size={15} className="mt-0.5 shrink-0 text-error" />
                      <div className="min-w-0 text-xs leading-relaxed text-text-title">
                        <p className="font-semibold text-error">
                          O cliente não vai receber esta mensagem
                        </p>
                        <p className="mt-0.5 text-text-secondary">
                          Passaram mais de 24h desde a última mensagem dele
                          {janela.estado === 'fechada' && ` (há ${duracaoCurta(janela.desdeMs)})`}.
                          O WhatsApp só entrega mensagem nova depois que o cliente escrever de novo.
                          Para falar antes disso, use outro canal.
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1 rounded-lg bg-main p-0.5">
                      <button
                        type="button"
                        onClick={() => setIsPrivate(false)}
                        className={cn(
                          'whitespace-nowrap rounded-md px-3 py-1 text-xs font-semibold transition-colors',
                          !isPrivate ? 'bg-primary text-white' : 'text-text-secondary'
                        )}
                      >
                        Responder
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsPrivate(true);
                          setGravandoAudio(false); // nota interna não leva áudio
                        }}
                        className={cn(
                          'whitespace-nowrap rounded-md px-3 py-1 text-xs font-semibold transition-colors',
                          isPrivate ? 'bg-amber-400 text-white' : 'text-text-secondary'
                        )}
                      >
                        Nota interna
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {/* Corrige o rascunho no lugar. Funciona pra resposta e pra
                          nota interna — é só ortografia/gramática. */}
                      <button
                        type="button"
                        onClick={() => {
                          // O resultado volta pro rascunho DESTA conversa, mesmo
                          // que o atendente troque de conversa enquanto espera.
                          const convId = selected.id;
                          correctDraftMutation.mutate(draft, {
                            onSuccess: (corrected) => setDraftDe(convId, corrected),
                            onError: (err) =>
                              avisar(err, 'Não foi possível corrigir o texto agora.'),
                          });
                        }}
                        disabled={!draft.trim() || correctDraftMutation.isPending}
                        title="Corrigir ortografia e gramática"
                        className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {correctDraftMutation.isPending ? (
                          <Loader2 className="animate-spin" size={13} />
                        ) : (
                          <SpellCheck2 size={13} />
                        )}
                        <span className="hidden lg:inline">Corrigir</span>
                      </button>
                      {/* Sugestão é resposta pro cliente — não faz sentido em
                          nota interna, que ninguém de fora vê. */}
                      {!isPrivate && (
                        <button
                          type="button"
                          onClick={() => {
                            const convId = selected.id;
                            suggestReplyMutation.mutate(convId, {
                              onSuccess: (suggestion) => setDraftDe(convId, suggestion),
                              onError: (err) =>
                                avisar(err, 'Não foi possível sugerir uma resposta agora.'),
                            });
                          }}
                          disabled={suggestReplyMutation.isPending}
                          title="Sugerir resposta com base na conversa"
                          className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {suggestReplyMutation.isPending ? (
                            <Loader2 className="animate-spin" size={13} />
                          ) : (
                            <Sparkles size={13} />
                          )}
                          <span className="hidden lg:inline">Sugerir</span>
                        </button>
                      )}
                      <div className="relative z-20">
                        <button
                          type="button"
                          onClick={() => setMenu(menu === 'canned' ? null : 'canned')}
                          className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-muted"
                        >
                          <Zap size={13} />{' '}
                          <span className="hidden lg:inline">Respostas rápidas</span>
                        </button>
                        {menu === 'canned' && (
                          <div className="absolute bottom-10 right-0 z-20 w-72 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
                            <p className="border-b border-border px-3 py-2 text-xs font-semibold text-text-title">
                              Respostas rápidas
                            </p>
                            <div className="max-h-64 overflow-y-auto py-1">
                              {(cannedQuery.data ?? []).map((c) => (
                                <button
                                  key={c.id}
                                  type="button"
                                  onClick={() => {
                                    setDraft((d) => (d ? d + '\n' + c.content : c.content));
                                    setMenu(null);
                                  }}
                                  className="block w-full px-3 py-1.5 text-left hover:bg-muted"
                                >
                                  <span className="block text-xs font-medium text-primary">
                                    /{c.short_code}
                                  </span>
                                  <span className="block truncate text-xs text-text-secondary">
                                    {c.content}
                                  </span>
                                </button>
                              ))}
                              {(cannedQuery.data ?? []).length === 0 && (
                                <p className="px-3 py-3 text-center text-xs text-text-tertiary">
                                  Nenhuma resposta rápida.
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Confirmação do anexo: o atendente vê o que vai mandar
                      antes de mandar. A legenda é o texto já digitado. */}
                  {anexo && (
                    <div className="mb-2 flex items-center gap-3 rounded-xl border border-border bg-main p-2">
                      {previaAnexo ? (
                        <img
                          src={previaAnexo}
                          alt=""
                          className="size-14 shrink-0 rounded-lg object-cover"
                        />
                      ) : (
                        <span className="flex size-14 shrink-0 items-center justify-center rounded-lg bg-card text-text-tertiary">
                          <Paperclip size={18} />
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-semibold text-text-title">
                          {anexo.name}
                        </p>
                        <p className="text-[11px] text-text-tertiary">
                          {formatarTamanho(anexo.size)}
                          {trechoVideo
                            ? ` · trecho de ${Math.round(trechoVideo.fim - trechoVideo.inicio)} s`
                            : ''}
                          {draft.trim() ? ' · com legenda' : ''}
                        </p>
                      </div>
                      {(anexoEhImagem || anexoEhVideo) && (
                        <button
                          type="button"
                          onClick={() => setEditando(anexoEhImagem ? 'imagem' : 'video')}
                          disabled={sendAttachmentMutation.isPending}
                          className="flex shrink-0 items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-semibold text-text-secondary hover:bg-muted disabled:opacity-40"
                        >
                          {anexoEhImagem ? <Pencil size={13} /> : <Scissors size={13} />}
                          {anexoEhImagem ? 'Editar' : 'Cortar'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setAnexo(null)}
                        disabled={sendAttachmentMutation.isPending}
                        className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-text-secondary hover:text-secondary disabled:opacity-40"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const convId = selected.id;
                          const caption = draft.trim();
                          sendAttachmentMutation.mutate(
                            {
                              convId,
                              file: anexo,
                              caption,
                              clientMessageId: novoIdDeEnvio(),
                              trecho: anexoEhVideo ? trechoVideo : null,
                            },
                            {
                              onSuccess: () => {
                                setAnexo(null);
                                if (caption)
                                  setDraftDe(convId, (atual) =>
                                    atual.trim() === caption ? '' : atual
                                  );
                              },
                              onError: (err) => avisar(err, 'Não foi possível enviar o arquivo.'),
                            }
                          );
                        }}
                        disabled={sendAttachmentMutation.isPending || janelaFechada}
                        className="flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                      >
                        {sendAttachmentMutation.isPending ? (
                          <>
                            <Loader2 className="animate-spin" size={12} /> Enviando
                          </>
                        ) : (
                          'Enviar arquivo'
                        )}
                      </button>
                    </div>
                  )}

                  <div className="flex items-end gap-2">
                    {gravandoAudio ? (
                      <AudioRecorder
                        enviando={sendAudioMutation.isPending}
                        onCancel={() => setGravandoAudio(false)}
                        onSend={(blob, filename) => {
                          const convId = selected.id;
                          sendAudioMutation.mutate(
                            { convId, blob, filename, clientMessageId: novoIdDeEnvio() },
                            {
                              onSuccess: () => setGravandoAudio(false),
                              // O áudio continua no gravador pra tentar de novo.
                              onError: (err) => avisar(err, 'Não foi possível enviar o áudio.'),
                            }
                          );
                        }}
                      />
                    ) : (
                      <>
                        <textarea
                          ref={draftRef}
                          value={draft}
                          onChange={(e) => {
                            setDraft(e.target.value);
                            // "Digitando" no WhatsApp do cliente. Só na resposta
                            // — nota interna ele nem vê. O hook já limita a
                            // frequência, então pode chamar a cada tecla.
                            if (!isPrivate && e.target.value.trim()) sinalizarDigitando();
                          }}
                          onPaste={(e) => {
                            // Print colado (Ctrl+V) vira anexo, pronto pra editar.
                            if (isPrivate) return;
                            const img = Array.from(e.clipboardData.files).find((f) =>
                              f.type.startsWith('image/')
                            );
                            if (img) {
                              e.preventDefault();
                              setAnexo(
                                new File(
                                  [img],
                                  img.name && img.name !== 'image.png'
                                    ? img.name
                                    : `print-${Date.now()}.png`,
                                  {
                                    type: img.type,
                                  }
                                )
                              );
                            }
                          }}
                          onKeyDown={(e) => {
                            // No celular o Enter quebra linha (o teclado não tem
                            // Shift fácil); lá envia pelo botão.
                            const toque = window.matchMedia('(pointer: coarse)').matches;
                            if (
                              e.key === 'Enter' &&
                              !e.shiftKey &&
                              !toque &&
                              !e.nativeEvent.isComposing
                            ) {
                              e.preventDefault();
                              if (!sendMutation.isPending) handleSend(e);
                            }
                          }}
                          aria-label={isPrivate ? 'Nota interna' : 'Resposta ao cliente'}
                          rows={1}
                          placeholder={
                            isPrivate ? 'Nota visível só pra equipe…' : 'Escreva uma resposta…'
                          }
                          className="max-h-[40vh] min-h-[2.75rem] flex-1 resize-y overflow-y-auto rounded-xl border border-border bg-main px-3.5 py-2.5 text-sm text-text-title outline-none placeholder:text-text-tertiary focus:border-primary"
                        />
                        {/* Anexo e áudio só fazem sentido pro cliente — nota
                            interna é texto. */}
                        {!isPrivate && (
                          <>
                            <input
                              ref={anexoInputRef}
                              type="file"
                              hidden
                              accept="image/jpeg,image/png,image/webp,video/mp4,video/3gpp,video/quicktime,.mov,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) setAnexo(f);
                                e.target.value = ''; // permite reescolher o mesmo arquivo
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => anexoInputRef.current?.click()}
                              disabled={sendMutation.isPending || janelaFechada}
                              aria-label="Anexar arquivo"
                              title={
                                janelaFechada
                                  ? 'Janela de 24h fechada — o cliente não receberia'
                                  : 'Enviar imagem, vídeo ou documento'
                              }
                              className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border text-text-secondary transition-colors hover:border-primary hover:text-primary disabled:opacity-40"
                            >
                              <Paperclip size={18} />
                            </button>
                            <BotaoGravar
                              onClick={() => setGravandoAudio(true)}
                              disabled={sendMutation.isPending || janelaFechada}
                            />
                          </>
                        )}
                        <button
                          type="submit"
                          disabled={
                            !draft.trim() || sendMutation.isPending || (janelaFechada && !isPrivate)
                          }
                          className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                        >
                          {sendMutation.isPending ? (
                            <Loader2 className="animate-spin" size={18} />
                          ) : (
                            <Send size={18} />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-between gap-3 border-t border-border bg-card p-3">
                  <span className="flex items-center gap-2 text-xs text-text-secondary">
                    <Lock size={14} className="text-text-tertiary" />
                    {isResolved
                      ? 'Atendimento encerrado. Reabra para responder, ou espere o cliente escrever.'
                      : takenByOther
                        ? `Em atendimento por ${assignee?.name}. Transfira para assumir.`
                        : 'Assuma a conversa para responder o cliente.'}
                  </span>
                  {!isResolved && (
                    <HeaderButton
                      onClick={() => assumir(selected.id)}
                      disabled={assignMutation.isPending || takenByOther}
                      variant="primary"
                      title="Assumir"
                    >
                      {assignMutation.isPending ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <UserPlus size={14} />
                      )}
                      Assumir
                    </HeaderButton>
                  )}
                </div>
              )}
            </>
          ) : selectedId != null ? (
            // Conversa escolhida mas ainda não está na lista desta aba (vindo de
            // notificação, ou mudou de status): o painel procura nas outras abas.
            // No celular a lista fica escondida, então o "voltar" tem que existir.
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-text-tertiary">
              <Loader2 className="animate-spin" size={22} />
              <p className="text-sm">Abrindo a conversa…</p>
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-text-secondary hover:bg-muted"
              >
                Voltar para a lista
              </button>
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-text-tertiary">
              <span className="flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <LifeBuoy size={30} />
              </span>
              <div>
                <p className="text-base font-semibold text-text-title">Central de atendimento</p>
                <p className="mx-auto mt-1 max-w-xs text-sm">
                  Selecione uma conversa à esquerda para responder o cliente.
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Painel do produtor: coluna fixa só em tela larga (>= 1360px). */}
        {selected && (
          <ProducerPanel
            phone={selected.meta?.sender?.phone_number}
            conversation={selected}
            className="hidden min-[1360px]:flex"
          />
        )}

        {/* Abaixo disso, abre por cima da conversa, como os dados do contato
            no Telegram e no Mensagens do iPad. */}
        {selected && painelContato && (
          <div
            className="fixed inset-0 z-50 flex justify-end bg-black/40 min-[1360px]:hidden"
            onClick={() => setPainelContato(false)}
          >
            <div
              role="dialog"
              aria-label="Dados do contato"
              onClick={(e) => e.stopPropagation()}
              className="flex h-full w-full max-w-sm animate-slide-in-right flex-col bg-card pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <p className="text-sm font-bold text-text-title">Dados do contato</p>
                <button
                  type="button"
                  onClick={() => setPainelContato(false)}
                  aria-label="Fechar dados do contato"
                  className="flex size-8 items-center justify-center rounded-lg text-text-secondary hover:bg-muted hover:text-text-title"
                >
                  <X size={18} />
                </button>
              </div>
              <ProducerPanel
                phone={selected.meta?.sender?.phone_number}
                conversation={selected}
                className="flex min-h-0 w-full flex-1 border-l-0"
              />
            </div>
          </div>
        )}
      </div>

      {/* Modal central de transferência — bloqueante, só fecha em "Assumir". */}
      {/* Confirmação de encerramento. Encerrar não é só mudar um status: manda
          a mensagem de despedida ao cliente e tira a conversa da fila. */}
      {confirmarEncerrar && selected && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center shadow-2xl">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-tertiary/10 text-tertiary">
              <CheckCircle2 size={22} />
            </div>
            <p className="mt-3 text-base font-bold text-text-title">Encerrar este atendimento?</p>
            <p className="mt-1 text-sm text-text-secondary">
              <span className="font-semibold text-text-title">
                {selected.meta?.sender?.name || 'O cliente'}
              </span>{' '}
              vai receber a mensagem de encerramento no WhatsApp. Se ele escrever de novo, a
              conversa reabre e passa pela triagem outra vez.
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmarEncerrar(false)}
                className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-text-secondary transition-colors hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  mudarStatus(selected.id, 'resolved');
                  setConfirmarEncerrar(false);
                }}
                disabled={toggleMutation.isPending}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-tertiary px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {toggleMutation.isPending ? (
                  <Loader2 className="animate-spin" size={15} />
                ) : (
                  <CheckCircle2 size={15} />
                )}
                Encerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingTransfer && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center shadow-2xl">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Forward size={22} />
            </div>
            <p className="mt-3 text-base font-bold text-text-title">
              Conversa transferida para você
            </p>
            <p className="mt-1 text-sm text-text-secondary">
              <span className="font-semibold text-text-title">
                {pendingTransfer.de || 'Um colega'}
              </span>{' '}
              transferiu o atendimento de{' '}
              <span className="font-semibold text-text-title">{pendingTransfer.nome}</span> para
              você.
            </p>
            <button
              type="button"
              onClick={() => acceptTransfer(pendingTransfer)}
              disabled={acceptTransferMutation.isPending}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-tertiary px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {acceptTransferMutation.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <UserPlus size={16} />
              )}
              Assumir atendimento
            </button>
          </div>
        </div>
      )}
      {triageAlert && !pendingTransfer && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="titulo-alerta-triagem"
          className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/60 p-4"
        >
          <div className="w-full max-w-sm rounded-2xl border border-secondary/30 bg-card p-6 text-center shadow-2xl">
            <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-secondary/15 text-secondary animate-pulse">
              <Bell size={22} />
            </span>
            <p id="titulo-alerta-triagem" className="mt-3 text-base font-bold text-text-title">
              Novo atendimento aguardando
            </p>
            <p className="mt-1 text-sm text-text-secondary">
              {triageAlert.title} concluiu a triagem e aguarda um atendente.
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => dispensarAlerta(triageAlert.convId)}
                className="flex-1 rounded-xl border border-border px-3 py-2.5 text-sm font-semibold text-text-secondary hover:bg-muted"
              >
                Dispensar
              </button>
              <button
                type="button"
                onClick={() => {
                  // Assume A CONVERSA DO ALERTA, pelo id dela — não a que estiver
                  // aberta na tela. Só abre depois que a API confirmou.
                  const id = triageAlert.convId;
                  assignMutation.mutate(
                    { convId: id, unassign: false },
                    {
                      onSuccess: () => {
                        dispensarAlerta(id);
                        openConversation(id);
                      },
                      onError: (err) => {
                        dispensarAlerta(id);
                        avisar(err, 'Não foi possível assumir este atendimento.');
                      },
                    }
                  );
                }}
                disabled={assignMutation.isPending}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-tertiary px-3 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {assignMutation.isPending && <Loader2 size={15} className="animate-spin" />}
                Assumir
              </button>
            </div>
          </div>
        </div>
      )}
      {editando === 'imagem' && anexo && (
        <EditorImagem
          arquivo={anexo}
          onCancelar={() => setEditando(null)}
          onConcluir={(editado) => {
            setAnexoBruto(editado);
            setEditando(null);
          }}
        />
      )}
      {editando === 'video' && anexo && (
        <EditorVideo
          arquivo={anexo}
          trechoInicial={trechoVideo}
          onCancelar={() => setEditando(null)}
          onConcluir={(trecho) => {
            setTrechoVideo(trecho);
            setEditando(null);
          }}
        />
      )}
      {midiaAberta != null && galeria.length > 0 && (
        <MediaViewer
          itens={galeria}
          indiceInicial={Math.min(midiaAberta, galeria.length - 1)}
          onFechar={() => setMidiaAberta(null)}
        />
      )}
      {/* Erro de qualquer ação, num lugar só e sempre visível. */}
      {aviso && (
        <div
          role="alert"
          className="fixed left-1/2 top-4 z-[140] flex w-[min(92vw,28rem)] -translate-x-1/2 items-start gap-2 rounded-xl border border-error/40 bg-card px-4 py-3 text-sm text-text-title shadow-2xl"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-error" />
          <span className="flex-1">{aviso}</span>
          <button
            type="button"
            onClick={() => setAviso(null)}
            aria-label="Fechar aviso"
            className="shrink-0 text-text-tertiary hover:text-text-title"
          >
            <X size={15} />
          </button>
        </div>
      )}
    </Layout>
  );
}
